import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { FlaskConical, Upload, UserCheck, Eye, RefreshCw, ExternalLink, AlertTriangle } from 'lucide-react';

export default function Laboratorio() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [referti, setReferti] = useState([]);
  const [ospiti, setOspiti] = useState([]);
  const [filtro, setFiltro] = useState('tutti');
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [assegna, setAssegna] = useState({ referto: null, ospite_id: '' });
  const token = localStorage.getItem('rsa_token');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filtro === 'tutti' ? {} : filtro === 'non_visti' ? { non_visti: true } : { stato: filtro };
      const [r, o] = await Promise.all([
        api.get('/referti-lab', { params }),
        api.get('/ospiti', { params: { stato: 'presente' } }),
      ]);
      setReferti(r.data);
      setOspiti(o.data);
    } catch { setReferti([]); }
    setLoading(false);
  }, [filtro]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    if (!file) { toast.error('Seleziona il file del referto'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/referti-lab/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.ospite_id) toast.success(`Referto assegnato a ${res.data.ospite_nome} (${res.data.match_motivo})`);
      else toast.warning(`Paziente non riconosciuto: ${res.data.match_motivo}. Assegnalo manualmente`);
      setShowUpload(false); setFile(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore caricamento'); }
    setUploading(false);
  };

  const confermaAssegna = async () => {
    if (!assegna.ospite_id) { toast.error('Seleziona l\'ospite'); return; }
    try {
      const r = await api.put(`/referti-lab/${assegna.referto.id}/assegna`, { ospite_id: assegna.ospite_id });
      toast.success(`Referto assegnato a ${r.data.ospite_nome}`);
      setAssegna({ referto: null, ospite_id: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const segnaVisto = async (id) => {
    try { await api.put(`/referti-lab/${id}/visto`); toast.success('Presa visione registrata'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const daAssegnare = referti.filter(r => r.stato === 'da_assegnare');
  const daVisionare = referti.filter(r => r.stato !== 'da_assegnare' && !r.visto_da_medico);

  return (
    <div className="space-y-5 max-w-[1600px] mx-auto" data-testid="laboratorio-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Laboratorio</h1>
          <p className="text-sm text-slate-500">Referti in arrivo, riconoscimento automatico del paziente e presa visione</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filtro} onValueChange={setFiltro}>
            <SelectTrigger className="w-44" data-testid="lab-filtro"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti i referti</SelectItem>
              <SelectItem value="da_assegnare">Da assegnare</SelectItem>
              <SelectItem value="non_visti">Da visionare</SelectItem>
              <SelectItem value="assegnato">Assegnati</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="lab-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
          <Dialog open={showUpload} onOpenChange={setShowUpload}>
            <DialogTrigger asChild>
              <Button className="bg-brand hover:bg-brand-dark text-white" data-testid="lab-page-upload-btn"><Upload className="w-4 h-4 mr-1" />Carica referto</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Carica Referto di Laboratorio</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">File (JPG, PNG, PDF - max 15 MB) *</Label>
                  <Input type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
                    onChange={e => setFile(e.target.files?.[0] || null)} data-testid="lab-page-file-input" />
                  <p className="text-[10px] text-slate-400 mt-1">L'IA legge il nominativo e assegna il referto all'ospite corrispondente</p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowUpload(false)}>Annulla</Button>
                  <Button onClick={handleUpload} disabled={uploading} className="bg-brand hover:bg-brand-dark text-white" data-testid="lab-page-save-btn">
                    {uploading ? 'Lettura in corso...' : 'Carica e analizza'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
          <p className="text-[10px] uppercase tracking-wide text-slate-400">Referti totali</p>
          <p className="text-2xl font-bold text-slate-900" data-testid="lab-totale">{referti.length}</p>
        </div>
        <div className="bg-white border border-amber-200 rounded-xl px-4 py-3">
          <p className="text-[10px] uppercase tracking-wide text-amber-500">Da assegnare</p>
          <p className="text-2xl font-bold text-amber-600" data-testid="lab-da-assegnare">{daAssegnare.length}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
          <p className="text-[10px] uppercase tracking-wide text-slate-400">Da visionare</p>
          <p className="text-2xl font-bold text-brand" data-testid="lab-da-visionare">{daVisionare.length}</p>
        </div>
        <div className="bg-white border border-red-200 rounded-xl px-4 py-3">
          <p className="text-[10px] uppercase tracking-wide text-red-400">Con valori alterati</p>
          <p className="text-2xl font-bold text-red-600">{referti.filter(r => r.valori_alterati > 0).length}</p>
        </div>
      </div>

      {loading ? <div className="text-center py-12 text-slate-400">Caricamento...</div> : referti.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-200 rounded-xl">
          <FlaskConical className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-xs text-slate-400">Nessun referto</p>
        </div>
      ) : (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Referti</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="lab-page-lista">
              {referti.map(r => (
                <div key={r.id} className={`p-3 rounded-lg border text-xs ${r.stato === 'da_assegnare' ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`} data-testid={`lab-page-item-${r.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800">
                        {r.ospite_nome || <span className="text-amber-700 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Paziente non riconosciuto</span>}
                        <span className="text-slate-400 font-normal"> · referto del {new Date(r.data_referto).toLocaleDateString('it-IT')}</span>
                      </p>
                      <p className="text-slate-500 mt-0.5">
                        {r.laboratorio || 'Laboratorio n/d'} · {(r.analiti || []).length} analiti
                        {r.valori_alterati > 0 && <span className="text-red-600 font-semibold"> · {r.valori_alterati} fuori range</span>}
                      </p>
                      <p className="text-[10px] text-slate-400">Nominativo letto: {r.paziente_letto || 'n/d'} · {r.match_motivo}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {!r.visto_da_medico && <Badge className="text-[9px] bg-amber-100 text-amber-700">da visionare</Badge>}
                      <Button size="sm" variant="outline" asChild>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/referti-lab/${r.id}/file?auth=${token}`} target="_blank" rel="noreferrer" data-testid={`lab-page-open-${r.id}`}>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </Button>
                      {r.ospite_id && (
                        <Button size="sm" variant="outline" onClick={() => navigate(`/ospiti/${r.ospite_id}?tab=lab`)} data-testid={`lab-page-goto-${r.id}`}>Cartella</Button>
                      )}
                      {!r.ospite_id && hasRole('coordinatore', 'medico', 'infermiere') && (
                        <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => setAssegna({ referto: r, ospite_id: '' })} data-testid={`lab-page-assegna-${r.id}`}>
                          <UserCheck className="w-3.5 h-3.5 mr-1" />Assegna
                        </Button>
                      )}
                      {hasRole('medico', 'coordinatore') && !r.visto_da_medico && r.ospite_id && (
                        <Button size="sm" className="bg-brand hover:bg-brand-dark text-white" onClick={() => segnaVisto(r.id)} data-testid={`lab-page-visto-${r.id}`}>
                          <Eye className="w-3.5 h-3.5 mr-1" />Visto
                        </Button>
                      )}
                    </div>
                  </div>
                  {r.sintesi_ai && <p className="text-slate-600 mt-1.5 border-t border-slate-100 pt-1.5">{r.sintesi_ai}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!assegna.referto} onOpenChange={v => !v && setAssegna({ referto: null, ospite_id: '' })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assegna referto all'ospite</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Nominativo letto sul referto: <b>{assegna.referto?.paziente_letto || 'n/d'}</b></p>
            <div>
              <Label className="text-xs">Ospite *</Label>
              <Select value={assegna.ospite_id} onValueChange={v => setAssegna(a => ({ ...a, ospite_id: v }))}>
                <SelectTrigger data-testid="lab-assegna-select"><SelectValue placeholder="Seleziona ospite" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {ospiti.map(o => <SelectItem key={o.id} value={o.id}>{o.cognome} {o.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAssegna({ referto: null, ospite_id: '' })}>Annulla</Button>
              <Button onClick={confermaAssegna} className="bg-brand hover:bg-brand-dark text-white" data-testid="lab-assegna-conferma">Assegna</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
